import { c as _c } from "react/compiler-runtime";
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCallback } from 'react';
import { listSkills } from 'shared/api/copilot';
import { useShouldShowInternalFeatures } from 'shared/hooks/useShouldShowInternalFeatures';
import { type Skill } from 'src/copilot/types/skills';
import { mapFlowSkillListToSkills } from 'src/copilot/utils/mapFlowSkill';
import { filterSkillsByInternalAccess } from 'src/copilot/utils/skills';
export const SKILLS_QUERY_KEY = 'copilot-skills';

/** Transient failures only — a hard miss must not look like an empty catalog forever. */
const SKILLS_MAX_RETRIES = 2;

/**
 * Skills list for Copilot Pro composer (slash + Skills menu).
 * Org-wide skills are dropped unless internal features are visible (role + profile toggle, same as
 * company-wide knowledge), so every consumer (picker, typed `/Name` pins, transcript pill tooltips)
 * only sees what the user may use.
 */
export const useSkills = (clientId, enabled) => {
  const $ = _c(10);
  const showInternalFeatures = useShouldShowInternalFeatures();
  let t0;
  if ($[0] !== showInternalFeatures) {
    t0 = skills => filterSkillsByInternalAccess(skills, showInternalFeatures);
    $[0] = showInternalFeatures;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const selectAllowedSkills = t0;
  let t1;
  let t2;
  if ($[2] !== clientId) {
    t1 = [SKILLS_QUERY_KEY, clientId];
    t2 = async () => mapFlowSkillListToSkills(await listSkills(clientId));
    $[2] = clientId;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  const t3 = enabled && Boolean(clientId);
  let t4;
  if ($[5] !== selectAllowedSkills || $[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      select: selectAllowedSkills,
      enabled: t3,
      retry: SKILLS_MAX_RETRIES
    };
    $[5] = selectAllowedSkills;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4);
};